/**
 * /dashboard/leave/$id — Leave-request detail (SOP §1.2;
 * Phase 3b — 2026-06-01).
 *
 * IMPORTANT — there is NO `GET /leave/requests/{id}` endpoint and no
 * leave-timeline table (deliberate; confirmed in the 4.2 backend audit).
 * The single request is DERIVED from the role-scoped list query
 * (useLeaveRequestFromList) — the same list the index page uses, so the
 * row reflects whatever the caller's SQL scope allows and stays
 * consistent after an approve/reject/cancel invalidation.
 *
 * The "timeline" here is NOT a synthetic event feed — it is the
 * request's own lifecycle fields (created_at → status, and if decided:
 * decided_by_id / decided_at / decision_notes) presented as an honest
 * status & decision summary. Approve/reject (approver) and cancel
 * (requester) are state+role gated in LeaveDecisionActions; the
 * backend is the boundary.
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/leave/[id]/page.tsx. Swaps:
 *   - "use client" dropped
 *   - use(params) (Next.js) → Route.useParams() (TanStack)
 *   - next/link → @tanstack/react-router Link (typed)
 */

import { useMemo } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, CalendarRange, FileText } from "lucide-react";
import {
  useLeaveRequestFromList,
  useLeaveTypes,
} from "@/hooks/use-leave";
import { useDirectory } from "@/hooks/use-directory";
import { toApiError } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtDays } from "@/lib/leave-format";
import { useAuth } from "@/lib/auth";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { LeaveDecisionActions } from "@/components/leave/leave-decision-actions";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/leave/$id")({
  component: LeaveRequestDetailRoute,
});

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-input px-3 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-gray-900">
        {value}
      </dd>
    </div>
  );
}

function LeaveRequestDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  // A malformed id ("foo") parses to NaN; useLeaveRequestFromList(NaN)
  // returns request = undefined which falls through to the
  // "Request not found" panel — same UX as a deleted / out-of-scope row.
  const numId = Number(id);
  const { user } = useAuth();
  const { request, isLoading, isError, error } =
    useLeaveRequestFromList(numId);
  const typesQ = useLeaveTypes();
  // Fallback id→name resolver; the request's own *_name is preferred.
  const dir = useDirectory({ includeInactive: true });

  const typeLabel = useMemo(() => {
    if (!request) return "";
    const t = typesQ.data?.leave_types.find(
      (x) => x.id === request.leave_type_id,
    );
    return t
      ? `${t.name} (${t.code})`
      : `Leave type #${request.leave_type_id}`;
  }, [request, typesQ.data]);

  return (
    <TimeAndLeaveShell
      title="Leave request"
      description={
        request
          ? `Filed on ${fmtDate(request.created_at)}.`
          : undefined
      }
    >
      <LeaveFeatureGate>
        <div className="mb-4 flex justify-end">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/leave" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        </div>

        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load this request</AlertTitle>
            <AlertDescription>
              {toApiError(error).message}
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && !request && (
          <Alert>
            <AlertTitle>Request not found</AlertTitle>
            <AlertDescription>
              This leave request doesn&apos;t exist or isn&apos;t
              visible to your role.{" "}
              <Link
                to="/app/$tenantSlug/leave" params={{ tenantSlug }}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Back to leave
              </Link>
              .
            </AlertDescription>
          </Alert>
        )}

        {request && (
          <div className="space-y-5">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">
                      {typeLabel} · {fmtDays(request.total_days)}
                    </CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {request.user_id === user?.user_id
                        ? "Your request"
                        : dir.label(request.user_id, request.user_name)}
                    </p>
                  </div>
                  <StatusBadge status={request.status} />
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Row
                    label="Dates"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
                        {fmtDate(request.start_date)} –{" "}
                        {fmtDate(request.end_date)}
                      </span>
                    }
                  />
                  <Row
                    label="Working days"
                    value={fmtDays(request.total_days)}
                  />
                  <Row
                    label="Submitted"
                    value={fmtDateTime(request.created_at)}
                  />
                  <Row label="Type" value={typeLabel} />
                </dl>
                {request.reason && (
                  <div className="mt-4 rounded-md border border-input bg-muted/30 p-3">
                    <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" />
                      Reason
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                      {request.reason}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status & decision summary — the honest "timeline": the
                request's own lifecycle fields, NOT a synthetic feed. */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Status &amp; decision
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Row
                    label="Current status"
                    value={<StatusBadge status={request.status} />}
                  />
                  {request.decided_at ? (
                    <>
                      <Row
                        label="Decided"
                        value={fmtDateTime(request.decided_at)}
                      />
                      <Row
                        label="Decided by"
                        value={
                          request.decided_by_id != null
                            ? dir.label(
                                request.decided_by_id,
                                request.decided_by_name,
                              )
                            : "—"
                        }
                      />
                      <Row
                        label="Decision note"
                        value={request.decision_notes || "—"}
                      />
                    </>
                  ) : (
                    <div className="rounded-md border border-dashed border-input px-3 py-2 text-sm text-muted-foreground sm:col-span-2">
                      {request.status === "pending"
                        ? "Awaiting a decision from the approver."
                        : request.status === "cancelled"
                          ? "This request was cancelled before any decision."
                          : "No decision recorded."}
                    </div>
                  )}
                </dl>

                <div className="pt-1">
                  <LeaveDecisionActions request={request} />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
